import type {
  EvidenceStatus,
  EvidenceStatusValue,
  IntakeFormData,
} from './types';

const hasText = (v: unknown): boolean =>
  typeof v === 'string' && v.trim().length > 0;

const toBool = (v: unknown): boolean => Boolean(v);

const asStatus = (status: EvidenceStatusValue, notes = ''): EvidenceStatus => ({
  status,
  notes,
});

export const computeCoverageSubRows = (
  form: IntakeFormData,
): {
  pip: EvidenceStatus;
  health: EvidenceStatus;
  thirdParty: EvidenceStatus;
} => {
  const pipAny =
    hasText(form.insurance.pipInsurance.companyName) ||
    hasText(form.insurance.pipInsurance.policyNumber);

  const healthAny = hasText(form.insurance.healthProvider);

  const defendants = form.insurance.defendants || [];
  const anyDefendantHasInfo = defendants.some((d) =>
    [
      d.insuranceCompanyName,
      d.policyHolderName,
      d.policyNumber,
      d.claimNumber,
      d.name,
    ].some(hasText),
  );

  return {
    pip: asStatus(pipAny ? 'CONFIRMED' : 'UNCERTAIN'),
    health: asStatus(healthAny ? 'CONFIRMED' : 'UNCERTAIN'),
    thirdParty: asStatus(anyDefendantHasInfo ? 'CONFIRMED' : 'UNCERTAIN'),
  };
};

const precedence: EvidenceStatusValue[] = [
  'CONFIRMED',
  'EXISTS_NOT_HAVE',
  'UNCERTAIN',
  'DOES_NOT_EXIST',
];

const combineInsurance = (subs: {
  pip: EvidenceStatus;
  health: EvidenceStatus;
  thirdParty: EvidenceStatus;
}): EvidenceStatus => {
  const subValues: EvidenceStatusValue[] = [
    subs.pip.status,
    subs.health.status,
    subs.thirdParty.status,
  ];
  const combined = (precedence.find((s) => subValues.includes(s)) ||
    'UNCERTAIN') as EvidenceStatusValue;
  return asStatus(combined);
};

export const computeEvidenceDefaults = (
  form: IntakeFormData,
): IntakeFormData['evidenceStatuses'] => {
  const photos = asStatus(
    toBool(form.incidentInfo.photos) ? 'CONFIRMED' : 'UNCERTAIN',
  );

  const witnesses = asStatus(
    hasText(form.incidentInfo.witnesses) ? 'CONFIRMED' : 'UNCERTAIN',
  );

  const hasPolice = toBool(form.incidentInfo.policeReport);
  const policeNumber = form.incidentInfo.policeReportNumber || '';
  let policeStatus: EvidenceStatusValue = 'UNCERTAIN';
  if (hasPolice && hasText(policeNumber)) policeStatus = 'CONFIRMED';
  else if (hasPolice) policeStatus = 'EXISTS_NOT_HAVE';
  const policeReport = asStatus(policeStatus);

  const ems = asStatus(
    toBool(form.incidentInfo.ems) ? 'CONFIRMED' : 'UNCERTAIN',
  );

  const hasMedical =
    hasText(form.incidentInfo.hospital) ||
    (form.medicalWageHistory.treatments || []).length > 0 ||
    toBool(form.incidentInfo.currentlyTreating);
  const medicalTreatment = asStatus(hasMedical ? 'CONFIRMED' : 'UNCERTAIN');

  const subs = computeCoverageSubRows(form);
  const insurance = combineInsurance(subs);

  // Legacy UI-only rows remain present but always UNCERTAIN by default
  const lostWages = asStatus('UNCERTAIN');
  const financialImpact = asStatus('UNCERTAIN');

  return {
    photos,
    witnesses,
    policeReport,
    ems,
    medicalTreatment,
    insurance,
    lostWages,
    financialImpact,
  };
};
